import { useState, useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import GreatJobBadge from '../Cards/GreatJobBadge';
import useTestResults from '../../hooks/useQuarantinedTests';
import SlidingWindow from '../SlidingWindow/SlidingWindow';
import './QuarantineTestsTable.css';

const QuarantinedTestsTable = ({ selectedPlatform, selectedUnit }) => {
  const { data, loading, error } = useTestResults(selectedPlatform, selectedUnit);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('fail_counter');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    // Handle date comparison
    if (orderBy === 'date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (bVal < aVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  };

  const sortedData = useMemo(() => {
    if (!data) return [];

    const getComparator = (order, orderBy) => {
      return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
    };

    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  const handleRowClick = (item) => {
    // Transform the quarantined test data to match the format expected by SlidingWindow
    const transformedItem = {
      testId: item.test_id,
      testName: item.name,
      repository: item.repository,
      unit: item.unit,
    };
    setSelectedItem(transformedItem);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedItem(null);
  };

  if (loading) return (
    <div>
      <h1 className="results-title">Quarantined tests</h1>
      <div className="results-container">
        <div className="results-table-container flashing loading"></div>
      </div>
    </div>
  );

  if (error) return (
    <div>
      <h1 className="results-title">Quarantined tests</h1>
      <div className="results-container">
        <div className="results-table-container flashing error"></div>
      </div>
    </div>
  );

  if (data.length === 0) return (
    <div>
      <h1 className="results-title">Quarantined tests</h1>
      <div className="results-container">
        <div className="quarantine-empty-state">
          <h1 className="no-failed-tests-header">No quarantined tests for selected platform</h1>
          <GreatJobBadge message="Every test is out of quarantine." />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="results-title">Quarantined tests</h1>
      <div className="results-container">
        <div className="results-table-container">
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="quarantined sticky tests table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '12%' }}>Test ID</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Test Name</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'repository'}
                      direction={orderBy === 'repository' ? order : 'asc'}
                      onClick={() => handleRequestSort('repository')}
                    >
                      Platform
                      {orderBy === 'repository' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'unit'}
                      direction={orderBy === 'unit' ? order : 'asc'}
                      onClick={() => handleRequestSort('unit')}
                    >
                      Unit
                      {orderBy === 'unit' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'date'}
                      direction={orderBy === 'date' ? order : 'asc'}
                      onClick={() => handleRequestSort('date')}
                    >
                      Failing since
                      {orderBy === 'date' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'last_update'}
                      direction={orderBy === 'last_update' ? order : 'asc'}
                      onClick={() => handleRequestSort('last_update')}
                    >
                      Last executed
                      {orderBy === 'last_update' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'fail_counter'}
                      direction={orderBy === 'fail_counter' ? order : 'asc'}
                      onClick={() => handleRequestSort('fail_counter')}
                    >
                      Fail counter
                      {orderBy === 'fail_counter' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'pass_counter'}
                      direction={orderBy === 'pass_counter' ? order : 'asc'}
                      onClick={() => handleRequestSort('pass_counter')}
                    >
                      Recovery
                      {orderBy === 'pass_counter' ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Jira</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleRowClick(item)}
                  >
                    <TableCell>
                      <ExternalLink size={16} className="expand-icon" />
                    </TableCell>
                    <TableCell>{item.test_id}</TableCell>
                    <TableCell>{item.tricentis_key ? `${item.tricentis_key} ${item.name}` : item.name}</TableCell>
                    <TableCell>{item.repository}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>{item.date.split("T")[0]}</TableCell>
                    <TableCell>{item.last_update.split("T")[0]}</TableCell>
                    <TableCell align="center">{item.fail_counter}</TableCell>
                    <TableCell align="center">
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: i < item.pass_counter ? '#4CAF50' : '#ddd',
                              transition: 'background-color 0.3s'
                            }}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{item.jira_bug_url || 'NA'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
      <SlidingWindow isOpen={isOpen} onClose={handleClose} content={<div>{JSON.stringify(selectedItem, null, 2)}</div>} />
    </div>
  );
};

export default QuarantinedTestsTable; 
